import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import businessDocumentType from "../../BusinessDocuments/businessDocumentType";
import BusinessDocumentItemSelectorV2Fragment, {
  type BusinessDocumentItemSelectorFragmentV2Handle,
} from "../../BusinessDocuments/BusinessDocumentItemSelector/BusinessDocumentItemSelectorFragmentV2";
import type businessDocumentItemVM from "../../BusinessDocuments/BusinessDocumentItemSelector/businessDocumentItemVM";
import getClassMetadata from "../../Common/classMetadataService";
import getDecimalMetadata from "../../Common/EditForm/ClassMetadata/getDecimalMetadata";
import DecimalInputFormRow from "../../Common/EditForm/DecimalInput/DecimalInputReactNumberFormatFormRow";
import FormButtons from "../../Common/EditForm/formButtons";
import LoadingContentWrapper from "../../Common/Loading/LoadingContentWrapper";
import type { ContractWithdrawalItemViewModel } from "./Vm/ContractWithdrawalItemViewModel";

export default function ContractWithdrawalItemForm(props: {
  values: ContractWithdrawalItemViewModel | null;
  onOk: (values: ContractWithdrawalItemViewModel) => void;
  onCancel: () => void;
  documentNumber: string;
  navigate: (url: string) => void;
}) {
  const defaultValues: ContractWithdrawalItemViewModel = {
    id: uuidv4(),
    documentItem: null,
    amount: null,
  };

  const [isLoading, setIsLoading] = useState(true);
  // biome-ignore lint/suspicious/noExplicitAny: metadata is server-dependent and has different properties for different field types
  const [metadata, setMetadata] = useState<{ [key: string]: any }>({});
  const isEditForm = props.values !== null;

  const methods = useForm<ContractWithdrawalItemViewModel>({
    mode: "onTouched",
    defaultValues,
  });

  const { formState, control, getValues, reset, handleSubmit, watch, trigger } =
    methods;
  const { isSubmitting } = formState;

  const businessDocumentItemSelectorRef =
    useRef<BusinessDocumentItemSelectorFragmentV2Handle>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: props.navigate is recreated by the route wrapper; adding it would repeatedly initialize and reset the form
  useEffect(() => {
    // Prevent work after this component is unmounted. The cleanup sets this to
    // false when, for example, the user returns to the main form before metadata loading finishes.
    let isMounted = true;

    const initialize = async () => {
      try {
        const metadataResponse = await getClassMetadata(
          "ContractWithdrawalItemVM",
        );
        if (!isMounted) return;

        setMetadata(metadataResponse);
        reset(
          props.values ?? {
            id: uuidv4(),
            documentItem: null,
            amount: null,
          },
        );
        setIsLoading(false);
      } catch (error) {
        if (!isMounted) return;

        console.error(error);
        props.navigate("/server-communication-error");
      }
    };

    initialize();

    return () => {
      isMounted = false;
    };
    // props.navigate is supplied by a wrapper as a new function on every render;
    // including it here would repeat initialization after each state update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.values, reset]);

  useEffect(() => {
    const subscription = watch(() => {
      trigger("amount", { shouldFocus: true });
    });
    return () => subscription.unsubscribe();
  }, [trigger, watch]);

  return (
    <div className="content-wrapper">
      <h1 className="border-bottom pb-2 mt-4 mb-5">
        {isEditForm
          ? "Edycja pozycji odstąpienia od umowy"
          : "Nowa pozycja odstąpienia od umowy"}
      </h1>
      <LoadingContentWrapper isLoading={isLoading}>
        {!isLoading && (
          <FormProvider {...methods}>
            <form noValidate>
              <div className="row">
                <div className="col-sm-8 offset-sm-4">
                  <h3>1. Wybór pozycji</h3>
                </div>
              </div>

              <BusinessDocumentItemSelectorV2Fragment
                name="documentItem"
                control={control}
                required={true}
                ref={businessDocumentItemSelectorRef}
                businessDocumentType={businessDocumentType.invoice}
                documentNumber={props.documentNumber}
              />

              <div className="row">
                <div className="col-sm-8 offset-sm-4">
                  <h3>2. Dane zwrotu</h3>
                </div>
              </div>

              <DecimalInputFormRow
                {...getDecimalMetadata<ContractWithdrawalItemViewModel>(
                  "amount",
                  metadata["amount"],
                )}
                label="Ilość do zwrotu *"
                longDescription="Ilość towaru objęta odstąpieniem od umowy (wymagane)."
                control={control}
                inlineDescription={undefined}
                rules={{
                  validate: {
                    lessThanOrEqualToAmountInLine: (value) => {
                      const documentItem: businessDocumentItemVM | null =
                        getValues("documentItem");
                      if (
                        documentItem !== null &&
                        value !== null &&
                        typeof value === "number" &&
                        value > documentItem.amount
                      ) {
                        return `Ilość do zwrotu nie może być większa niż ilość na pozycji faktury, tj. ${documentItem.amount}.`;
                      }
                      return undefined;
                    },
                  },
                }}
              />

              <div className="row pt-2">
                <div className="offset-sm-4 col-sm-8">
                  <FormButtons
                    onSave={(e) => handleSubmit(props.onOk)(e)}
                    onCancel={props.onCancel}
                    isSubmitting={isSubmitting}
                  />
                </div>
              </div>
            </form>
          </FormProvider>
        )}
      </LoadingContentWrapper>
    </div>
  );
}
